import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit
} from "firebase/firestore";
import {
    ChevronRight,
    ArrowLeft,
    ThumbsUp,
    Utensils,
    Coffee,
    Pizza,
    MapPin,
    Clock,
    Heart,
    Search,
    Filter,
    X,
    Check,
    Sparkles,
    FastForward,
    ChevronDown,
    RefreshCw,
    PieChart,
    Tag,
    Smile,
    Zap,
    Award,
    CheckCircle2,
    Info,
    Plus
} from 'lucide-react';

const FoodQuiz = () => {
    // Estados para datos de Firebase
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados para el cuestionario interactivo
    const [quizStep, setQuizStep] = useState(0);
    const [showResults, setShowResults] = useState(false);
    const [quizSelections, setQuizSelections] = useState({
        mood: '',
        time: '',
        categories: [],
        subcategories: []
    });
    const [matchedRestaurants, setMatchedRestaurants] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [favorites, setFavorites] = useState([]);
    const [stats, setStats] = useState({
        totalRestaurants: 0,
        totalCategories: 0,
        totalSubcategories: 0
    });

    // Cargar datos de Firebase al inicio
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Obtener categorías
                const categoriesSnapshot = await getDocs(
                    query(collection(db, "categories"), orderBy("name"))
                );
                const categoriesData = categoriesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setCategories(categoriesData);

                // Obtener subcategorías
                const subcategoriesSnapshot = await getDocs(
                    query(collection(db, "subcategories"), orderBy("name"))
                );
                const subcategoriesData = subcategoriesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setSubcategories(subcategoriesData);

                // Obtener restaurantes
                const restaurantsSnapshot = await getDocs(
                    query(collection(db, "restaurants"), orderBy("name"))
                );
                const restaurantsData = restaurantsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    isFavorite: false
                }));
                setRestaurants(restaurantsData);

                // Actualizar estadísticas
                setStats({
                    totalRestaurants: restaurantsData.length,
                    totalCategories: categoriesData.length,
                    totalSubcategories: subcategoriesData.length
                });

                // Cargar favoritos del local storage
                const storedFavorites = localStorage.getItem('restaurantFavorites');
                if (storedFavorites) {
                    const parsedFavorites = JSON.parse(storedFavorites);
                    setFavorites(parsedFavorites);

                    // Marcar restaurantes favoritos
                    const updatedRestaurants = restaurantsData.map(restaurant => ({
                        ...restaurant,
                        isFavorite: parsedFavorites.includes(restaurant.id)
                    }));
                    setRestaurants(updatedRestaurants);
                }

                setIsLoading(false);
            } catch (err) {
                console.error("Error fetching data: ", err);
                setError("Error al cargar los datos: " + err.message);
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Opciones de estado de ánimo para el primer paso
    const moodOptions = [
        { id: "happy", label: "Feliz", icon: <Smile size={28} />, color: "from-yellow-400 to-orange-500" },
        { id: "hungry", label: "Hambriento", icon: <Utensils size={28} />, color: "from-red-400 to-pink-500" },
        { id: "adventurous", label: "Aventurero", icon: <Zap size={28} />, color: "from-purple-400 to-indigo-500" },
        { id: "relaxed", label: "Relajado", icon: <Coffee size={28} />, color: "from-blue-400 to-cyan-500" }
    ];

    // Opciones de tiempo para el segundo paso
    const timeOptions = [
        { id: "quick", label: "Comida rápida", icon: <Zap size={24} />, color: "from-yellow-400 to-amber-500" },
        { id: "normal", label: "Tiempo normal", icon: <Clock size={24} />, color: "from-blue-400 to-teal-500" },
        { id: "leisure", label: "Sin prisa", icon: <Coffee size={24} />, color: "from-indigo-400 to-purple-500" }
    ];

    // Manejar selección de categoría
    const handleCategorySelect = (categoryId) => {
        setQuizSelections(prev => {
            const updatedCategories = prev.categories.includes(categoryId)
                ? prev.categories.filter(id => id !== categoryId)
                : [...prev.categories, categoryId];

            // Al cambiar las categorías, limpiamos las subcategorías que ya no son relevantes
            const relevantRestaurants = restaurants.filter(restaurant =>
                restaurant.categoryIds &&
                restaurant.categoryIds.some(id => updatedCategories.includes(id))
            );

            const relevantSubcategoryIds = new Set();
            relevantRestaurants.forEach(restaurant => {
                if (restaurant.subcategoryIds && restaurant.subcategoryIds.length > 0) {
                    restaurant.subcategoryIds.forEach(id => relevantSubcategoryIds.add(id));
                }
            });

            const updatedSubcategories = prev.subcategories.filter(id =>
                relevantSubcategoryIds.has(id)
            );

            return {
                ...prev,
                categories: updatedCategories,
                subcategories: updatedSubcategories
            };
        });
    };

    // Manejar selección de subcategoría
    const handleSubcategorySelect = (subcategoryId) => {
        setQuizSelections(prev => {
            const updatedSubcategories = prev.subcategories.includes(subcategoryId)
                ? prev.subcategories.filter(id => id !== subcategoryId)
                : [...prev.subcategories, subcategoryId];

            return {
                ...prev,
                subcategories: updatedSubcategories
            };
        });
    };

    // Manejar cambio de favorito
    const handleToggleFavorite = (restaurantId) => {
        // Actualizar lista de restaurantes
        const updatedRestaurants = restaurants.map(restaurant => {
            if (restaurant.id === restaurantId) {
                return {
                    ...restaurant,
                    isFavorite: !restaurant.isFavorite
                };
            }
            return restaurant;
        });
        setRestaurants(updatedRestaurants);

        // Actualizar lista de favoritos
        const updatedFavorites = updatedRestaurants
            .filter(restaurant => restaurant.isFavorite)
            .map(restaurant => restaurant.id);
        setFavorites(updatedFavorites);

        // También actualizar la lista de restaurantes encontrados
        setMatchedRestaurants(prev =>
            prev.map(restaurant =>
                restaurant.id === restaurantId
                    ? { ...restaurant, isFavorite: !restaurant.isFavorite }
                    : restaurant
            )
        );

        // Guardar en localStorage
        localStorage.setItem('restaurantFavorites', JSON.stringify(updatedFavorites));
    };

    // Aplicar filtros y encontrar restaurantes que coincidan
    const findMatchingRestaurants = () => {
        let filtered = [...restaurants];

        // Filtrar por categorías seleccionadas en el cuestionario
        if (quizSelections.categories.length > 0) {
            filtered = filtered.filter(restaurant =>
                restaurant.categoryIds &&
                restaurant.categoryIds.some(id => quizSelections.categories.includes(id))
            );
        }

        // Filtrar por subcategorías seleccionadas en el cuestionario
        if (quizSelections.subcategories.length > 0) {
            filtered = filtered.filter(restaurant =>
                restaurant.subcategoryIds &&
                restaurant.subcategoryIds.some(id => quizSelections.subcategories.includes(id))
            );
        }

        // Filtrar por estado de ánimo (esto es opcional, puedes implementar tu propia lógica)
        if (quizSelections.mood) {
            // Aquí podrías implementar una lógica personalizada basada en el estado de ánimo
            // Ejemplo: Si está "feliz", priorizar restaurantes con ciertas categorías
        }

        // Filtrar por tiempo disponible (esto es opcional, puedes implementar tu propia lógica)
        if (quizSelections.time) {
            // Ejemplo: Si seleccionó "rápido", priorizar restaurantes de comida rápida
        }

        // Filtrar por término de búsqueda si existe
        if (searchTerm.trim() !== "") {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(restaurant =>
                restaurant.name.toLowerCase().includes(term) ||
                (restaurant.categoryNames &&
                    restaurant.categoryNames.some(cat =>
                        cat.toLowerCase().includes(term)
                    ))
            );
        }

        setMatchedRestaurants(filtered);
    };

    // Avanzar al siguiente paso
    const goToNextStep = () => {
        if (quizStep < 3) {
            // Si vamos al paso de subcategorías, verificamos si hay disponibles
            if (quizStep === 2) {
                const filteredSubcategories = getFilteredSubcategories();
                // Si no hay subcategorías disponibles, saltamos directamente a los resultados
                if (filteredSubcategories.length === 0) {
                    finishQuiz();
                    return;
                }
            }
            setQuizStep(quizStep + 1);
        } else {
            finishQuiz();
        }
    };

    // Volver al paso anterior
    const goToPrevStep = () => {
        if (showResults) {
            // Si se llegó a los resultados desde el paso de categorías (saltando subcategorías)
            // debemos volver al paso de categorías
            if (quizStep === 2) {
                setShowResults(false);
                return;
            }
            // En caso contrario, volvemos al paso de subcategorías
            setShowResults(false);
            return;
        }

        if (quizStep > 0) {
            setQuizStep(quizStep - 1);
        }
    };

    // Finalizar el cuestionario y mostrar resultados
    const finishQuiz = () => {
        findMatchingRestaurants();
        setShowResults(true);
    };

    // Reiniciar el cuestionario
    const resetQuiz = () => {
        setQuizStep(0);
        setQuizSelections({
            mood: '',
            time: '',
            categories: [],
            subcategories: []
        });
        setSearchTerm("");
        setShowResults(false);
    };

    // Obtener subcategorías filtradas por categorías seleccionadas
    const getFilteredSubcategories = () => {
        if (quizSelections.categories.length === 0) {
            return subcategories;
        }

        // Filtramos las subcategorías basándonos en los restaurantes que tienen
        // las categorías seleccionadas
        const relevantRestaurants = restaurants.filter(restaurant =>
            restaurant.categoryIds &&
            restaurant.categoryIds.some(id => quizSelections.categories.includes(id))
        );

        // Obtenemos todas las subcategorías utilizadas por estos restaurantes
        const relevantSubcategoryIds = new Set();
        relevantRestaurants.forEach(restaurant => {
            if (restaurant.subcategoryIds && restaurant.subcategoryIds.length > 0) {
                restaurant.subcategoryIds.forEach(id => relevantSubcategoryIds.add(id));
            }
        });

        // Filtramos la lista de subcategorías para mostrar solo las relevantes
        return subcategories.filter(subcategory => relevantSubcategoryIds.has(subcategory.id));
    };

    // Obtener color aleatorio pero consistente basado en texto
    const getColorForText = (text) => {
        const colors = [
            "bg-pink-100 text-pink-800 border-pink-200",
            "bg-purple-100 text-purple-800 border-purple-200",
            "bg-indigo-100 text-indigo-800 border-indigo-200",
            "bg-blue-100 text-blue-800 border-blue-200",
            "bg-cyan-100 text-cyan-800 border-cyan-200",
            "bg-teal-100 text-teal-800 border-teal-200",
            "bg-emerald-100 text-emerald-800 border-emerald-200",
            "bg-green-100 text-green-800 border-green-200",
            "bg-lime-100 text-lime-800 border-lime-200",
            "bg-yellow-100 text-yellow-800 border-yellow-200",
            "bg-amber-100 text-amber-800 border-amber-200",
            "bg-orange-100 text-orange-800 border-orange-200",
            "bg-red-100 text-red-800 border-red-200"
        ];

        // Simple hash function para seleccionar color
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
            hash = ((hash << 5) - hash) + text.charCodeAt(i);
            hash = hash & hash;
        }

        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    // Generar color de fondo para tarjeta
    const getCardGradient = (id) => {
        const gradients = [
            "from-rose-500/10 to-pink-500/10",
            "from-purple-500/10 to-violet-500/10",
            "from-indigo-500/10 to-blue-500/10",
            "from-blue-500/10 to-cyan-500/10",
            "from-teal-500/10 to-emerald-500/10",
            "from-emerald-500/10 to-green-500/10",
            "from-lime-500/10 to-yellow-500/10",
            "from-amber-500/10 to-orange-500/10",
            "from-orange-500/10 to-red-500/10"
        ];

        // Use the ID to select a consistent gradient
        const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % gradients.length;
        return gradients[index];
    };

    // Renderizar paso 1: Selección de estado de ánimo
    const renderMoodSelection = () => (
        <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">¿Cómo te sientes hoy?</h2>
            <p className="text-gray-600">Selecciona tu estado de ánimo y te ayudaremos a encontrar la comida perfecta</p>

            <div className="grid grid-cols-2 gap-3">
                {moodOptions.map(mood => (
                    <button
                        key={mood.id}
                        onClick={() => setQuizSelections({ ...quizSelections, mood: mood.id })}
                        className={`bg-gradient-to-r ${mood.color} text-white p-4 rounded-xl flex flex-col items-center justify-center transition-all duration-200 ${quizSelections.mood === mood.id
                            ? 'ring-2 ring-white ring-offset-2 scale-[1.02]'
                            : 'opacity-90 hover:opacity-100 hover:scale-[1.01]'
                            }`}
                    >
                        <div className="bg-white/20 p-3 rounded-full mb-2">
                            {mood.icon}
                        </div>
                        <span className="font-medium">{mood.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    // Renderizar paso 2: Selección de tiempo disponible
    const renderTimeSelection = () => (
        <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">¿Cuánto tiempo tienes?</h2>
            <p className="text-gray-600">Esto nos ayudará a recomendarte el lugar perfecto</p>

            <div className="grid grid-cols-1 gap-3">
                {timeOptions.map(option => (
                    <button
                        key={option.id}
                        onClick={() => setQuizSelections({ ...quizSelections, time: option.id })}
                        className={`bg-gradient-to-r ${option.color} text-white p-4 rounded-xl flex items-center transition-all duration-200 ${quizSelections.time === option.id
                            ? 'ring-2 ring-white ring-offset-2 scale-[1.02]'
                            : 'opacity-90 hover:opacity-100 hover:scale-[1.01]'
                            }`}
                    >
                        <div className="bg-white/20 p-3 rounded-full mr-4">
                            {option.icon}
                        </div>
                        <span className="font-medium">{option.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    // Renderizar paso 3: Selección de categorías
    // Renderizar paso 3: Selección de categorías
    const renderCategorySelection = () => (
        <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">¿Qué te gustaría comer?</h2>
            <p className="text-gray-600">Selecciona una o más categorías</p>

            <div className="flex flex-col space-y-3">
                {categories.map(category => {
                    const isSelected = quizSelections.categories.includes(category.id);
                    return (
                        <button
                            key={category.id}
                            onClick={() => handleCategorySelect(category.id)}
                            className={`p-4 rounded-xl border transition-all duration-200 flex items-center justify-between ${isSelected
                                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <span className="font-medium truncate">{category.name}</span>
                            {isSelected ? (
                                <Check size={18} className="text-green-500 ml-2 flex-shrink-0" />
                            ) : (
                                <Plus size={18} className="text-gray-400 ml-2 flex-shrink-0" />
                            )}
                        </button>
                    );
                })}
            </div>

            {quizSelections.categories.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-sm text-gray-500 mr-1">Seleccionados:</span>
                    {quizSelections.categories.map(catId => {
                        const category = categories.find(c => c.id === catId);
                        return category && (
                            <span
                                key={catId}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                            >
                                {category.name}
                                <button
                                    onClick={() => handleCategorySelect(catId)}
                                    className="ml-1.5 text-indigo-600 hover:text-indigo-800"
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );

    // Renderizar paso 4: Selección de subcategorías
    // Renderizar paso 4: Selección de subcategorías
    const renderSubcategorySelection = () => {
        const filteredSubcategories = getFilteredSubcategories();

        return (
            <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-800">¿Algo más específico?</h2>
                <p className="text-gray-600">Selecciona las subcategorías que te interesen (opcional)</p>

                {filteredSubcategories.length > 0 ? (
                    <div className="flex flex-col space-y-3">
                        {filteredSubcategories.map(subcategory => {
                            const isSelected = quizSelections.subcategories.includes(subcategory.id);
                            return (
                                <button
                                    key={subcategory.id}
                                    onClick={() => handleSubcategorySelect(subcategory.id)}
                                    className={`p-3 rounded-xl border transition-all duration-200 flex items-center justify-between ${isSelected
                                        ? 'bg-purple-50 border-purple-200 text-purple-700'
                                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <span className="font-medium truncate">{subcategory.name}</span>
                                    {isSelected ? (
                                        <Check size={18} className="text-green-500 ml-2 flex-shrink-0" />
                                    ) : (
                                        <Plus size={18} className="text-gray-400 ml-2 flex-shrink-0" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center p-6 bg-gray-50 rounded-xl">
                        <p className="text-gray-500">No hay subcategorías disponibles para las categorías seleccionadas.</p>
                    </div>
                )}
            </div>
        );
    };

    // Renderizar tarjeta de restaurante
    const renderRestaurantCard = (restaurant) => {
        const gradientClass = getCardGradient(restaurant.id);

        return (
            <div
                key={restaurant.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-[1.02]"
            >
                <div className={`h-32 bg-gradient-to-r ${gradientClass} relative flex items-center justify-center`}>
                    <div className="absolute top-3 right-3 z-10">
                        <button
                            onClick={() => handleToggleFavorite(restaurant.id)}
                            className={`p-2 rounded-full shadow-sm ${restaurant.isFavorite ? 'bg-white text-red-500' : 'bg-white/90 text-gray-400 hover:text-gray-600'} transition-all duration-200 hover:scale-110`}
                            aria-label={restaurant.isFavorite ? "Eliminar de favoritos" : "Añadir a favoritos"}
                        >
                            <Heart fill={restaurant.isFavorite ? "currentColor" : "none"} size={18} strokeWidth={2} />
                        </button>
                    </div>
                    <div className="text-gray-300">
                        <Coffee size={38} />
                    </div>
                </div>
                <div className="p-3">
                    <h3 className="font-bold text-gray-800 mb-2">{restaurant.name}</h3>

                    <div className="flex flex-wrap gap-1.5 min-h-[40px] mb-3">
                        {restaurant.categoryNames && restaurant.categoryNames.slice(0, 2).map((cat, index) => (
                            <span key={index} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getColorForText(cat)}`}>
                                {cat}
                            </span>
                        ))}
                        {restaurant.categoryNames && restaurant.categoryNames.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                +{restaurant.categoryNames.length - 2}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center">
                            <MapPin size={12} className="mr-1" />
                            <span>Ubicación</span>
                        </div>
                        <div className="flex items-center">
                            <Clock size={12} className="mr-1" />
                            <span>Abierto</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Renderizar resultados
    const renderResults = () => (
        <div className="space-y-6 pb-20">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold">¡Tus resultados!</h2>
                    <div className="bg-white/20 rounded-full px-3 py-1 text-sm">
                        {matchedRestaurants.length} {matchedRestaurants.length === 1 ? 'restaurante' : 'restaurantes'}
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    {quizSelections.categories.map(catId => {
                        const category = categories.find(c => c.id === catId);
                        return category && (
                            <span
                                key={catId}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/20 text-white"
                            >
                                {category.name}
                            </span>
                        );
                    })}

                    {quizSelections.subcategories.map(subId => {
                        const subcategory = subcategories.find(s => s.id === subId);
                        return subcategory && (
                            <span
                                key={subId}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white"
                            >
                                {subcategory.name}
                            </span>
                        );
                    })}
                </div>
            </div>

            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={18} className="text-gray-400" />
                </div>
                <input
                    type="text"
                    className="bg-white w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    placeholder="Buscar en los resultados..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setTimeout(() => findMatchingRestaurants(), 300);
                    }}
                />
            </div>

            {matchedRestaurants.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                    {matchedRestaurants.map(restaurant => renderRestaurantCard(restaurant))}
                </div>
            ) : (
                <div className="text-center p-8 bg-gray-50 rounded-xl">
                    <div className="inline-flex items-center justify-center p-3 bg-gray-100 rounded-full text-gray-500 mb-4">
                        <Info size={24} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">No encontramos coincidencias</h3>
                    <p className="text-gray-500 max-w-xs mx-auto mb-4">
                        Prueba con diferentes categorías o ajusta tus filtros para encontrar opciones.
                    </p>
                    <button
                        onClick={resetQuiz}
                        className="inline-flex items-center px-4 py-2 rounded-xl bg-indigo-600 text-white"
                    >
                        <RefreshCw size={16} className="mr-2" />
                        Reiniciar búsqueda
                    </button>
                </div>
            )}
        </div>
    );

    // Barra de progreso
    const renderProgressBar = () => {
        const totalSteps = 4;
        const currentStep = showResults ? totalSteps : quizStep;
        const progress = ((currentStep + 1) / (totalSteps + 1)) * 100;

        return (
            <div className="w-full h-1.5 bg-gray-100 rounded overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
        );
    };

    // Pantalla de carga
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mb-4"></div>
                    <h1 className="text-xl font-bold text-gray-800 mb-2">Preparando tu experiencia</h1>
                    <p className="text-gray-600">Cargando opciones deliciosas para ti...</p>
                </div>
            </div>
        );
    }

    // Pantalla de error
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <div className="inline-flex items-center justify-center p-3 bg-red-100 rounded-full text-red-500 mb-4">
                        <X size={32} />
                    </div>
                    <h1 className="text-xl font-bold text-gray-800 mb-2">¡Ups! Algo salió mal</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium"
                    >
                        Intentar de nuevo
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-md mx-auto p-4">
                {/* Header con progreso */}
                <div className="sticky top-0 bg-gray-50 pt-4 pb-3 z-10">
                    <div className="flex items-center justify-between mb-3">
                        <h1 className="text-xl font-bold text-gray-800">Food Quiz 1.0 by pacgc :)</h1>
                        {(quizStep > 0 || showResults) && (
                            <button
                                onClick={goToPrevStep}
                                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                            >
                                <ArrowLeft size={20} />
                            </button>
                        )}
                    </div>
                    {renderProgressBar()}
                </div>

                {/* Contenido principal */}
                <div className="py-4">
                    {!showResults ? (
                        <>
                            {quizStep === 0 && renderMoodSelection()}
                            {quizStep === 1 && renderTimeSelection()}
                            {quizStep === 2 && renderCategorySelection()}
                            {quizStep === 3 && renderSubcategorySelection()}
                        </>
                    ) : (
                        renderResults()
                    )}
                </div>

                {/* Botón de acción - Movido para que no se oculte detrás de las estadísticas */}
                {!showResults && (
                    <div className="sticky bottom-20 pt-3 pb-6 bg-gray-50 z-20">
                        <button
                            onClick={goToNextStep}
                            disabled={(quizStep === 0 && quizSelections.mood === "") || (quizStep === 1 && quizSelections.time === "") || (quizStep === 2 && quizSelections.categories.length === 0)}
                            className={`w-full py-3 px-4 rounded-xl flex items-center justify-center font-medium transition-all duration-200 ${(quizStep === 0 && quizSelections.mood === "") || (quizStep === 1 && quizSelections.time === "") || (quizStep === 2 && quizSelections.categories.length === 0)
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg active:shadow-sm'
                                }`}
                        >
                            {quizStep === 3 ? (
                                <>Ver resultados <Sparkles size={18} className="ml-2" /></>
                            ) : (
                                <>Continuar <ChevronRight size={18} className="ml-1" /></>
                            )}
                        </button>
                    </div>
                )}

                {/* Componente para volver al inicio rápidamente */}
                {showResults && (
                    <div className="sticky bottom-20 right-6 flex justify-end z-20">
                        <button
                            onClick={resetQuiz}
                            className="p-3 rounded-full bg-white shadow-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                            aria-label="Reiniciar cuestionario"
                        >
                            <RefreshCw size={20} />
                        </button>
                    </div>
                )}
            </div>

            {/* Estadísticas en miniatura - Cambiadas a posición fija para que no oculten el botón */}
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-md p-3 border-t border-gray-200 flex justify-between items-center text-xs text-gray-600 z-10">
                <div className="flex items-center">
                    <Utensils size={14} className="mr-1 text-indigo-500" />
                    <span>{stats.totalRestaurants} restaurantes</span>
                </div>
                <div className="flex items-center">
                    <Tag size={14} className="mr-1 text-emerald-500" />
                    <span>{stats.totalCategories} categorías</span>
                </div>
                <div className="flex items-center">
                    <PieChart size={14} className="mr-1 text-amber-500" />
                    <span>{stats.totalSubcategories} subcategorías</span>
                </div>
            </div>
        </div>
    );
};

export default FoodQuiz;